import React, { useState, useMemo, useEffect } from 'react';
import './Guide.css';
import { useSite } from '../context/SiteContext';
import { useT, useI18n } from '../i18n';
import LangSwitcher from '../components/LangSwitcher';

// 优先用管理员在后台配置的 public_url,未配置则 fallback 到当前浏览器地址
const fallbackOrigin = (typeof window !== 'undefined' && window.location) ? window.location.origin : '';

// 工具列表(badge 是产品话术,i18n 化)
const TOOLS = [
  { id: 'cc-switch',   name: 'CC Switch',         badgeKey: 'guide.tool.ccswitch.badge' },
  { id: 'claude-code', name: 'Claude Code',       badgeKey: 'guide.tool.claudecode.badge' },
  { id: 'openclaw',    name: 'OpenClaw',          badgeKey: 'guide.tool.openclaw.badge' },
  { id: 'opencode',    name: 'OpenCode',          badgeKey: 'guide.tool.opencode.badge' },
  { id: 'curl',        nameKey: '指南.Curl',       badgeKey: 'guide.tool.curl.badge' },
];

const OSES = [
  { id: 'windows', name: '🪟 Windows' },
  { id: 'mac',     name: 'macOS' },
  { id: 'linux',   name: '🐧 Linux' },
];

// 示例模型列表(可在后台渠道配置)
const MODELS = [
  { id: 'Minimax-m3',            name: 'MiniMax-M3',             ctx: 1000000 },
  { id: 'Minimax-m2.7',          name: 'MiniMax-M2.7',           ctx: 204800  },
  { id: 'Minimax-m2.7-highspeed',name: 'MiniMax-M2.7 HighSpeed',  ctx: 204800  },
];

// 按钮文案和 key 模板走 i18n
function buildConfig({ tool, os, model, key, lang }) {
  const placeholderKey = lang === 'en-US' ? '<YOUR_API_KEY>' : '<你的 API Key>';
  const placeholderModel = lang === 'en-US' ? '<model>' : '<模型>';
  if (!key) key = placeholderKey;
  const m = MODELS.find(x => x.id === model);
  const modelName = m ? m.name : placeholderModel;
  const ctx = m ? m.ctx : 204800;

  // 注释走 i18n(代码示例里的中文改成对应语言)
  const label = lang === 'en-US';
  const cTest   = label ? '# Test if the API is available' : '# 测试 API 是否可用';
  const cCla    = label ? `# ${tool === 'claude-code' ? 'Claude Code' : tool === 'openclaw' ? 'OpenClaw' : 'CC Switch'} config` : `# ${tool === 'claude-code' ? 'Claude Code' : tool === 'openclaw' ? 'OpenClaw' : 'CC Switch'} 配置`;
  const cAnMod  = label ? '# Anthropic compatible mode'    : '# Anthropic 兼容模式';
  const cCtxHdr = label ? '# Context length (per model)'    : '# 上下文长度(按所选模型)';
  const cOCfg   = label ? '# OpenCode config'               : '# OpenCode 配置';
  const cGen    = label ? '# Integration config'            : '# 接入配置';

  switch (tool) {
    case 'curl':
      return `${cTest}\ncurl -X POST "${origin}/v1/chat/completions" \\\n  -H "Content-Type: application/json" \\\n  -H "Authorization: Bearer ${key}" \\\n  -d '{"model":"${modelName}","messages":[{"role":"user","content":"hello"}]}'`;
    case 'claude-code':
    case 'cc-switch':
    case 'openclaw':
      return `${cCla}\n${cAnMod}\n\nANTHROPIC_BASE_URL=${origin}/v1\nANTHROPIC_AUTH_TOKEN=${key}\nANTHROPIC_MODEL=${modelName}\n${cCtxHdr}\n# ${modelName}: ${ctx}\nANTHROPIC_CONTEXT_LENGTH=${ctx}`;
    case 'opencode':
      return `${cOCfg}\n\nOPENCODE_BASE_URL=${origin}/v1\nOPENCODE_API_KEY=${key}\nOPENCODE_MODEL=${modelName}`;
    default:
      return `${cGen}\nBase URL: ${origin}/v1\nAPI Key: ${key}\nModel: ${modelName}`;
  }
}

const Guide = () => {
  const { site } = useSite();
  const t = useT();
  const { lang } = useI18n();
  const [step, setStep] = useState(1);
  const [apiKey, setApiKey] = useState('');
  const [tool, setTool] = useState('cc-switch');
  const [os, setOs] = useState('windows');
  const [model, setModel] = useState('minimax-m3');
  const [copied, setCopied] = useState(false);
  const [publicUrl, setPublicUrl] = useState('');

  useEffect(() => {
    fetch('/api/status')
      .then((r) => r.json())
      .then((j) => {
        const u = (j && j.data && j.data.public_url) || '';
        setPublicUrl(u.replace(/\/+$/, ''));
      })
      .catch(() => {});
  }, []);

  const origin = publicUrl || fallbackOrigin;

  const code = useMemo(
    () => buildConfig({ tool, os, model, key: apiKey, lang }),
    [tool, os, model, apiKey, origin, lang]
  );

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  const canProceed = {
    1: apiKey.trim().length >= 5,
    2: !!tool,
    3: !!os,
    4: !!model,
    5: true,
  };

  const steps = [
    { n: 1, title: t('指南.步骤1标题'), desc: t('指南.步骤1描述') },
    { n: 2, title: t('指南.步骤2标题'), desc: t('指南.步骤2描述') },
    { n: 3, title: t('指南.步骤3标题'), desc: t('指南.步骤3描述') },
    { n: 4, title: t('指南.步骤4标题'), desc: t('指南.步骤4描述') },
    { n: 5, title: t('指南.步骤5标题'), desc: t('指南.步骤5描述') },
  ];

  return (
    <div className="guide-page">
      {/* 顶部导航(与主页一致: logo + site_name · 接入指南) */}
      <header className="navbar">
        <div className="navbar__inner">
          <a href="/" className="navbar__brand">
            <img src="/logo.svg" alt="" className="navbar__logo" />
            <span className="navbar__title">{site.site_name} · {t('导航.接入指南')}</span>
          </a>
          <div className="navbar__actions">
            <LangSwitcher variant="navbar" />
          </div>
        </div>
      </header>

      <main className="guide-main">
        <div className="guide-important">
          <div className="guide-important__title">{t('指南.重要提示')}</div>
          <ul className="guide-important__list">
            <li>{t('指南.重要提示1')}</li>
            <li>{t('指南.重要提示2')}</li>
          </ul>
        </div>

        {/* 步骤条 */}
        <div className="guide-steps-bar">
          {steps.map((s) => {
            const active = step === s.n;
            const done = step > s.n;
            return (
              <div
                key={s.n}
                className={`guide-step-indicator ${active ? 'is-active' : ''} ${done ? 'is-done' : ''}`}
                onClick={() => setStep(s.n)}
              >
                <div className="guide-step-indicator__num">{done ? '✓' : s.n}</div>
                <div className="guide-step-indicator__text">{s.title}</div>
              </div>
            );
          })}
        </div>

        {/* 步骤内容 */}
        <div className="guide-step-card">
          {step === 1 && (
            <StepCard num={1} title={t('指南.步骤1标题')}>
              <p className="guide-desc">{t('指南.步骤1详细')} <code>sk-xxxxx</code></p>
              <input
                className="guide-input"
                type="text"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={t('指南.步骤1占位符')}
              />
            </StepCard>
          )}

          {step === 2 && (
            <StepCard num={2} title={t('指南.步骤2标题')}>
              <div className="guide-grid guide-grid--tools">
                {TOOLS.map((it) => (
                  <button
                    key={it.id}
                    className={`guide-tile ${tool === it.id ? 'is-selected' : ''}`}
                    onClick={() => setTool(it.id)}
                  >
                    {it.badgeKey.includes('推荐') && <span className="guide-tile__badge">{t('指南.推荐')}</span>}
                    <div className="guide-tile__name">{it.nameKey ? t(it.nameKey) : it.name}</div>
                    <div className="guide-tile__sub">{t(it.badgeKey)}</div>
                  </button>
                ))}
              </div>
            </StepCard>
          )}

          {step === 3 && (
            <StepCard num={3} title={t('指南.步骤3标题')}>
              <div className="guide-grid guide-grid--os">
                {OSES.map((o) => (
                  <button
                    key={o.id}
                    className={`guide-tile guide-tile--compact ${os === o.id ? 'is-selected' : ''}`}
                    onClick={() => setOs(o.id)}
                  >
                    {o.name}
                  </button>
                ))}
              </div>
            </StepCard>
          )}

          {step === 4 && (
            <StepCard num={4} title={t('指南.步骤4标题')}>
              <div className="guide-grid guide-grid--models">
                {MODELS.map((m) => (
                  <button
                    key={m.id}
                    className={`guide-tile ${model === m.id ? 'is-selected' : ''}`}
                    onClick={() => setModel(m.id)}
                  >
                    <div className="guide-tile__name">{m.name}</div>
                    <div className="guide-tile__sub">{t('指南.上下文')} {m.ctx.toLocaleString()}</div>
                  </button>
                ))}
              </div>
            </StepCard>
          )}

          {step === 5 && (
            <StepCard num={5} title={t('指南.步骤5标题')}>
              <div className="guide-code-wrap">
                <div className="guide-code-header">
                  <span className="guide-code-title">
                    {TOOLS.find(it => it.id === tool)?.nameKey ? t(TOOLS.find(it => it.id === tool).nameKey) : (TOOLS.find(it => it.id === tool)?.name || t('指南.配置'))}
                  </span>
                  <button className="guide-btn guide-btn--ghost" onClick={copy}>
                    {copied ? t('公共.已复制') : t('公共.复制')}
                  </button>
                </div>
                <pre className="guide-code"><code>{code}</code></pre>
                <div className="guide-code-tip">{t('指南.粘贴提示')}</div>
              </div>
            </StepCard>
          )}
        </div>

        {/* 导航按钮 */}
        <div className="guide-nav">
          {step > 1 && (
            <button className="guide-btn guide-btn--ghost" onClick={() => setStep(step - 1)}>{t('公共.上一步')}</button>
          )}
          {step < 5 && canProceed[step] && (
            <button className="guide-btn guide-btn--primary" onClick={() => setStep(step + 1)}>{t('公共.下一步')}</button>
          )}
          {step < 5 && !canProceed[step] && (
            <button className="guide-btn guide-btn--primary guide-btn--disabled" disabled>{t('公共.下一步')}</button>
          )}
        </div>

        {/* 底部保障 */}
        <div className="guide-guarantee">
          <div className="guide-guarantee__header">
            <h3 className="guide-guarantee__title">{t('指南.售后保障')}</h3>
            {site.support_enabled && site.support_content && (
              <button className="guide-btn guide-btn--green" onClick={() => alert(site.support_content)}>{t('导航.联系客服')}</button>
            )}
          </div>
          <ul className="guide-guarantee__list">
            <li>{t('指南.售后保障1')}</li>
            <li>{t('指南.售后保障2')}</li>
          </ul>
        </div>
      </main>
    </div>
  );
};

const StepCard = ({ num, title, children }) => (
  <div className="guide-step-card">
    <div className="guide-step-header">
      <span className="guide-step-num">{num}</span>
      <h2 className="guide-step-title">{title}</h2>
    </div>
    {children}
  </div>
);

export default Guide;